import React from 'react';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Table, TableBody, TableCell, TableContainer, TableRow, TableHead, TextField, Chip, Alert, Box, TableFooter, InputAdornment, Tabs, Tab, Paper } from '@mui/material';
import CardTravelIcon from '@mui/icons-material/CardTravel';
import TimeToLeaveIcon from '@mui/icons-material/TimeToLeave';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Skeleton from '@mui/material/Skeleton';
import LoadingSimpleBackdrop from '../../tools/LoadingSimpleBackdrop';
import Message from '../../tools/Message';
import { formatDate } from '../../../helpers/FormateDate';
import MissionSummaryService from '../../../services/MissionSummaryService';
import MissionTransportService from '../../../services/MissionTransportService';
import TransportData from './TransportData';
import ProvisionalFuelMissionService from '../../../services/ProvisionalFuelMissionService';
import formatWithThousandSeparator from '../../../helpers/FormatWithThousandSeparator';
import SuccesDialog from '../../../components/reusable/SuccesDialog';
import ErrorDialog from '../../../components/reusable/ErrorDialog';
import { useFuelProvisionalConfirmStyles } from './FuelProvisionalConfirm.styles';
import RentalChargeTable from './RentalChargeTable';

const LOCATION_FIELDS = {
  depart: {
    region: 'region_depart',
    district: 'district_depart',
    commune: 'commune_depart',
    fokontany: 'fokontany_depart',
  },
  arrivee: {
    region: 'region_arrivee',
    district: 'district_arrivee',
    commune: 'commune_arrivee',
    fokontany: 'fokontany_arrivee',
  },
};

const FuelProvisionalConfirm = () => {
  const { mission_planning_id, timesheet_id, agenda_id, provisional_fuel_id } = useParams();
  const navigate = useNavigate();
  const classes = useFuelProvisionalConfirmStyles();
  const [mission, setMission] = useState(null);
  const [loadingBackdrop, setLoadingBackdrop] = useState(false);
  const [isLoadingSkeleton, setIsLoadingSkeleton] = useState(false);
  const [agendas, setAgendas] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [disabled, setDisabled] = useState(false);
  const [missionnaries, setMissionnaries] = useState([]);
  const [transports, setTransports] = useState([]);
  const [selectedTransportTab, setSelectedTransportTab] = useState(0);
  const [repartitions, setRepartitions] = useState({});
  const [modifiedDistributionRows, setModifiedDistributionRows] = useState({});
  const [callbackDialog, setCallbackDialog] = useState({ type: "", open: false, message: '', onClose: () => setCallbackDialog({ ...callbackDialog, open: false, message: '' }) });
  const [provisionalFuel, setProvisionalFuel] = useState(null);
  const [message, setMessage] = useState({
      open: false,
      color: 'green',
      messageInfo: '',
    });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    mission_planning_id: mission_planning_id || null,
    agenda_id: agenda_id || null,
    timesheet_id: timesheet_id || null,
  });
  const [destDialog, setDestDialog] = useState({ open: false, agenda_id: null, onClose: () => setDestDialog({ ...destDialog, open: false, agenda_id: null }), missionnaries: [] });
  const [removeDialog, setRemoveDialog] = useState({ open: false, agenda_id: null, mission_destination_id: null, mission_timesheet_destination_id: null, onClose: () => setRemoveDialog({ ...removeDialog, open: false, agenda_id: null, mission_destination_id: null, mission_timesheet_destination_id: null }) });
  const [viewDialog, setViewDialog] = useState({ open: false, destination: null, index: null, editable: false });
 const { open, color, messageInfo } = message;
    function handleCloseMessage() {
    setMessage({ ...message, open: false });
  }
  const fetchMission = async () => {
    try {
      setLoadingBackdrop(true);
      const response = await MissionSummaryService.getMissionData(mission_planning_id, agenda_id);
      if(response && response.status){
        setMission(response.data);
        setAgendas(response.data.agendas || []);
        setMissionnaries(response.data.missionnaries || []);
      }
    }
    catch (error) {
      console.error("Error fetching mission data:", error);
    }
    finally {
      setLoadingBackdrop(false);
    }
  };

  const fetchDestinations = async (transport_id, mission_rent_id = null) => { // hydration des destinations
      setIsLoadingSkeleton(true);
    try {
     const response = await ProvisionalFuelMissionService.fuelProvisionnalByTransport(transport_id, mission_rent_id);
      if(response && response.status){
        const destinationsResult = response.data || [];

      setDestinations((prevDestinations) => [...prevDestinations, {
        transport_id: transport_id,
        destinations: destinationsResult
      }]);

    }
    }
    catch (error) {
      console.error("Error fetching mission data:", error);
    }
    finally {
      setIsLoadingSkeleton(false);
    }
  };

   const getProvisional = async () => {
    try {
      const provisionfuel = await ProvisionalFuelMissionService.getProvisionalFuelMission(provisional_fuel_id);
      if(provisionfuel && provisionfuel.status){
        setProvisionalFuel(provisionfuel.data);
      } else {
       setMessage({ open: true, color: 'red', messageInfo: provisionfuel?.message || "Une erreur est survenue lors de la recuperation du carburant provisionnel." });
      }
    } catch (error) {
      console.error("Error fetching provisional fuel mission:", error);
      setMessage({ open: true, color: 'red', messageInfo: "Une erreur est survenue lors de la recuperation du carburant provisionnel." });
    }
  }

  const fetchTransportsMissions = async (mission_id, agenda_id) => {
    setLoadingBackdrop(true);
    try {
      const response = await MissionTransportService.transportsByMissionPlanning(mission_id, agenda_id);
      if(response && response.status){
        setTransports(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching transport means:", error);
    } finally {
      setLoadingBackdrop(false);
    }
  };      
 
  useEffect(() => { // fetch mission data
    fetchMission();
    if (mission_planning_id && agenda_id && timesheet_id > 0 && provisional_fuel_id) {
      fetchTransportsMissions(mission_planning_id, agenda_id);
    }
  }, [mission_planning_id, agenda_id]);

  useEffect(() => { // fetch destinations data
    if (transports.length > 0){
      for (const transport of transports){
        fetchDestinations(transport.transport_id, transport.mission_rent_id || null);
      }
    }   
  }, [transports]);

  useEffect(() => {
    if (!transports.length) {
      setSelectedTransportTab(0);
      return;
    }
    if (selectedTransportTab > transports.length - 1) {
      setSelectedTransportTab(transports.length - 1);
    }
  }, [transports, selectedTransportTab]);

  useEffect(() => {
      if(provisional_fuel_id){
        getProvisional();
      }
    }, [provisional_fuel_id]);

  const getLocationFieldKey = (type, level) => LOCATION_FIELDS[type][level];

  const handleConfirmDistrubution = async () => {
    setLoadingBackdrop(true);
    setDisabled(true);
    try {
      let provisionalFuelId = mission?.provisional_fuel_id || null;
      if (!provisionalFuelId && timesheet_id) {
        const timesheetResult = await ProvisionalFuelMissionService.getTimesheetById(timesheet_id);
        provisionalFuelId = timesheetResult?.data?.provisional_fuel_id || null;
      }

      if (!provisionalFuelId) {
        setMessage({
          open: true,
          color: 'red',
          messageInfo: 'Provisional fuel introuvable pour cette mission.',
        });
        return;
      }

      const payloads = {
        provisional_fuel_id: provisionalFuelId,
        mission_planning_id: mission_planning_id,
        mission_planning_agenda_id: agenda_id,
        mission_timesheet_id: timesheet_id,
        distributions: [],
      };
      let hasInvalidDistribution = false;
      let hasMissingFunding = false;

      destinations.forEach((destEntry) => {
        const transport = transports.find(
          (item) => item.transport_id === destEntry.transport_id
        );
        if (!transport) {
          return;
        }
        (destEntry.destinations || []).forEach((destination, index) => {
          const lineFinancements = getDestinationLineFinancements(destination, transport);
          if (!lineFinancements.length) {
            hasMissingFunding = true;
            return;
          }

          const rowKey = buildRowKey(destEntry.transport_id, destination, index);
          const rowValues = repartitions[rowKey] || {};
          const percentages = lineFinancements.map((fin, finIndex) => {
            const finKey = buildFinancementKey(fin, finIndex);
            const rawValue =
              rowValues[finKey] !== undefined ? rowValues[finKey] : fin?.percentage_rate;
            return parseNumber(rawValue, 0);
          });
          const total = percentages.reduce((sum, value) => sum + value, 0);
          if (Math.abs(total - 100) > 0.01) {
            hasInvalidDistribution = true;
            return;
          }

          const baseAmount = parseNumber(destination?.mission_timesheet_details_amount, 0);
          lineFinancements.forEach((fin, finIndex) => {
            const percent = percentages[finIndex] || 0;
            payloads.distributions.push({
              provisional_fuel_id: provisionalFuelId,
              mission_timesheet_destination_id: destination?.mission_timesheet_destination_id ?? null,
              id_financement_mis: fin?.id_financement_mis ?? null,
              code_financement_mis: fin?.code_financement_mis ?? null,
              percentage_rate: percent,
              amount: getDistributionAmount(rowKey, fin, baseAmount, percent),
              transport_id: transport.transport_id,
            });
          });
        });
      });

      if (hasMissingFunding) {
        setMessage({
          open: true,
          color: 'red',
          messageInfo: 'Une ou plusieurs lignes  aucun financement resolu.',
        });
        return;
      }

      if (hasInvalidDistribution) {
        setMessage({
          open: true,
          color: 'red',
          messageInfo: 'Chaque repartition doit totaliser 100%.',
        });
        return;
      }

      if (!payloads.distributions.length) {
        setMessage({
          open: true,
          color: 'red',
          messageInfo: 'Aucune repartition a confirmer.',
        });
        return;
      }

      const result =  await ProvisionalFuelMissionService.confirmFuelProvisionnalMission(payloads);
      if (result && result.status) {
        setMessage({
          open: true,
          color: 'green',
          messageInfo: 'Distribution de carburant confirmee.',
        });
        setCallbackDialog({
          type: "success",
          open: true,
          message: 'Distribution de carburant confirmee. Le décompte prévisionnels de carburant est maintenant en attente de validation.',
          onClose: () => setCallbackDialog({ ...callbackDialog, open: false, message: '' })
        });
        getProvisional();
        
      } else {
        setMessage({
          open: true,
          color: 'red',
          messageInfo: result?.message || 'Erreur lors de la confirmation de la distribution.',
        });
        setCallbackDialog({
          type: "error",
          open: true,
          message: result?.message || 'Erreur lors de la confirmation de la distribution.',
          onClose: () => setCallbackDialog({ ...callbackDialog, open: false, message: '' })
        });
      }
    } catch (error) {
      console.error('Error confirming distribution:', error);
      setMessage({
        open: true,
        color: 'red',
        messageInfo: 'Erreur lors de la confirmation de la distribution.',
      });
    } finally {
      setLoadingBackdrop(false);
      setDisabled(false);
    }
  };

  const getLocationLabel = (entry, destination, type, level) => {
    const key = getLocationFieldKey(type, level);
    return entry?.[key] || destination?.[key] || '-';
  };

  const getCombinedLocationLabel = (entry, destination, type) => {
    const levels = ['region', 'district', 'commune', 'fokontany'];
    const locationParts = levels
      .map((level) => getLocationLabel(entry, destination, type, level))
      .filter((part) => part && part !== '-');

    const specificLabel =
      type === 'depart'
        ? (destination?.lieu_depart || destination?.direction_location || '')
        : (destination?.lieu_arrivee || destination?.direction_location || '');

    const fullParts = specificLabel ? [...locationParts, specificLabel] : locationParts;
    return fullParts.length ? fullParts.join(' / ') : '-';
  };

  const parseNumber = (value, fallback = 0) => {
    const parsed = parseFloat((value ?? '').toString().replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const roundToNearestHundred = (value) =>
    Math.round(parseNumber(value, 0) / 100) * 100;

  const getDistributionAmount = (rowKey, lineFinancement, baseAmount, percentage) => {
    if (!modifiedDistributionRows[rowKey]) {
      return roundToNearestHundred(lineFinancement?.amount);
    }

    return roundToNearestHundred(
      (parseNumber(baseAmount, 0) * parseNumber(percentage, 0)) / 100
    );
  };

  const formatPercentValue = (value) => {
    const numeric = parseNumber(value, 0);
    return Number.isInteger(numeric) ? numeric.toString() : numeric.toFixed(2);
  };

  const getTransportFinancements = (transport) => {
    const list = Array.isArray(transport?.financements) ? transport.financements : [];
    return list;
  };

  const buildFinancementKey = (fin, index = 0) => {
    if (!fin) {
      return `fin-${index}`;
    }
    const code = (fin?.code_financement_mis ?? '').toString().trim();
    const hasId =
      fin?.id_financement_mis !== null &&
      fin?.id_financement_mis !== undefined &&
      `${fin?.id_financement_mis}` !== '' &&
      !Number.isNaN(Number(fin?.id_financement_mis));

    if (hasId || code) {
      return `${hasId ? Number(fin?.id_financement_mis) : ''}|${code}`;
    }
    return `fin-${index}`;
  };

  const buildRowKey = (transportId, destination, index) =>
    destination?.mission_timesheet_destination_id ??
    destination?.mission_destination_id ??
    `${transportId}-${index}`;

  const getFinancementPercentValue = (count) => {
    if (!count) {
      return '';
    }
    const value = 100 / count;
    const label = Number.isInteger(value) ? value.toString() : value.toFixed(2);
    return label;
  };

  const getRepartitionValue = (rowKey, finKey, fallback = '') => {
    const rowValues = repartitions[rowKey] || {};
    if (rowValues[finKey] === undefined) {
      return fallback;
    }
    return rowValues[finKey];
  };

  const getTransportDestinationRows = (transportId) =>
    destinations
      .filter((destEntry) => destEntry.transport_id === transportId)
      .flatMap((destEntry) => destEntry.destinations || []);

  const normalizeFinancementEntry = (fin, defaultPercent = 0) => ({
    id_financement_mis: Number.isFinite(Number(fin?.id_financement_mis))
      ? Number(fin.id_financement_mis)
      : 0,
    code_financement_mis:
      (fin?.code_financement_mis ?? '').toString().trim() || 'SANS_CODE_FINANCEMENT',
    percentage_rate: parseNumber(fin?.percentage_rate, defaultPercent),
    amount: parseNumber(fin?.amount, 0),
  });

  const getDestinationLineFinancements = (destination, transport) => {
    const backendLineFinancements = Array.isArray(destination?.line_financements)
      ? destination.line_financements
      : [];
    if (backendLineFinancements.length) {
      const defaultPercent = parseNumber(
        getFinancementPercentValue(backendLineFinancements.length),
        0
      );
      return backendLineFinancements.map((fin) =>
        normalizeFinancementEntry(fin, defaultPercent)
      );
    }

    if (
      destination?.id_financement_mis !== undefined ||
      destination?.code_financement_mis !== undefined
    ) {
      return [
        normalizeFinancementEntry(
          {
            id_financement_mis: destination?.id_financement_mis ?? 0,
            code_financement_mis: destination?.code_financement_mis ?? 'SANS_CODE_FINANCEMENT',
            percentage_rate: 100,
            amount: destination?.mission_timesheet_details_amount ?? 0,
          },
          100
        ),
      ];
    }

    const transportFinancements = getTransportFinancements(transport);
    if (!transportFinancements.length) {
      return [];
    }
    const defaultPercent = parseNumber(
      getFinancementPercentValue(transportFinancements.length),
      0
    );
    return transportFinancements.map((fin) => normalizeFinancementEntry(fin, defaultPercent));
  };

  const getTransportFinancementColumns = (transport, transportDestinations) => {
    const columnsByKey = new Map();
    (transportDestinations || []).forEach((destination) => {
      const lineFinancements = getDestinationLineFinancements(destination, transport);
      lineFinancements.forEach((financement, index) => {
        const key = buildFinancementKey(financement, index);
        if (!columnsByKey.has(key)) {
          columnsByKey.set(key, financement);
        }
      });
    });
    return Array.from(columnsByKey.values());
  };

  const handleRepartitionChange = (rowKey, finKey, activeKeys, nextValue) => {
    setModifiedDistributionRows((prev) => ({ ...prev, [rowKey]: true }));
    setRepartitions((prev) => {
      const current = { ...(prev[rowKey] || {}) };
      if (nextValue === '') {
        current[finKey] = '';
        return { ...prev, [rowKey]: current };
      }
      const numeric = parseFloat(nextValue);
      if (Number.isNaN(numeric)) {
        return prev;
      }
      const clamped = Math.min(Math.max(numeric, 0), 100);
      current[finKey] = formatPercentValue(clamped);
      const otherKeys = (activeKeys || []).filter((key) => key !== finKey);
      if (otherKeys.length) {
        const remaining = Math.max(0, 100 - clamped);
        const perOther = remaining / otherKeys.length;
        const formatted = formatPercentValue(perOther);
        otherKeys.forEach((key) => {
          current[key] = formatted;
        });
      }
      return { ...prev, [rowKey]: current };
    });
  };

  useEffect(() => {
    if (!transports.length || !destinations.length) {
      return;
    }
    setRepartitions((prev) => {
      const next = { ...prev };
      destinations.forEach((destEntry) => {
        const transport = transports.find(
          (item) => item.transport_id === destEntry.transport_id
        );
        if (!transport) {
          return;
        }
        (destEntry.destinations || []).forEach((destination, index) => {
          const rowKey = buildRowKey(destEntry.transport_id, destination, index);
          const lineFinancements = getDestinationLineFinancements(destination, transport);
          if (!lineFinancements.length) {
            next[rowKey] = {};
            return;
          }
          const existing = next[rowKey] || {};
          const updated = {};
          lineFinancements.forEach((fin, finIndex) => {
            const key = buildFinancementKey(fin, finIndex);
            const fallbackPercent = formatPercentValue(fin?.percentage_rate);
            updated[key] = existing[key] !== undefined ? existing[key] : fallbackPercent;
          });
          next[rowKey] = updated;
        });
      });
      return next;
    });
  }, [transports, destinations]);
  const selectedTransport = transports[selectedTransportTab] || null;
  const transportDestinations = selectedTransport
    ? getTransportDestinationRows(selectedTransport.transport_id)
    : [];
  const transportFinancementColumns = selectedTransport
    ? getTransportFinancementColumns(selectedTransport, transportDestinations)
    : [];
  const financementColumns = transportFinancementColumns.length
    ? transportFinancementColumns
    : [null];
  const isLastTransportTab =
    transports.length > 0 && selectedTransportTab === transports.length - 1;
  const isPendingState = provisionalFuel?.provisional_fuel_state === "PENDING";
  const refusalObservation = String(provisionalFuel?.observation || '').trim();
  const hasTimesheetContext =
    Number(timesheet_id) > 0 && Number(provisional_fuel_id) > 0;
  const totalDistanceKm = transportDestinations.reduce(
    (total, dest) => total + parseNumber(dest?.distance_km, 0),
    0
  );
  const totalConsumption = transportDestinations.reduce(
    (total, dest) => total + parseNumber(dest?.mission_timesheet_details_consumption, 0),
    0
  );
  const transportPricePerLiter = parseNumber(selectedTransport?.price_per_liter, 0);
  const financementTotals = transportDestinations.reduce((acc, dest, index) => {
    if (!selectedTransport) {
      return acc;
    }

    const rowKey = buildRowKey(selectedTransport.transport_id, dest, index);
    const rowLineFinancements = getDestinationLineFinancements(dest, selectedTransport);
    const rowLineByKey = rowLineFinancements.reduce((mapByKey, financement, finIndex) => {
      mapByKey[buildFinancementKey(financement, finIndex)] = financement;
      return mapByKey;
    }, {});
    const baseAmount = parseNumber(dest?.mission_timesheet_details_amount, 0);

    financementColumns.forEach((financement, finIndex) => {
      if (!financement) {
        return;
      }
      const finKey = buildFinancementKey(financement, finIndex);
      const lineFinancement = rowLineByKey[finKey];
      if (!lineFinancement) {
        return;
      }

      const fallbackPercent = formatPercentValue(lineFinancement?.percentage_rate);
      const value = getRepartitionValue(rowKey, finKey, fallbackPercent);
      const percent = parseNumber(value, 0);
      const distributionAmount = getDistributionAmount(
        rowKey,
        lineFinancement,
        baseAmount,
        percent
      );
      acc[finKey] = (acc[finKey] || 0) + distributionAmount;
    });

    return acc;
  }, {});
  const totalAmount = Object.values(financementTotals).reduce(
    (total, amount) => total + parseNumber(amount, 0),
    0
  );

  return (
    <Box className={classes.root}>
    <Paper className={classes.paper}>
      <Message open={open} handleClose={handleCloseMessage} color={color} messageInfo={messageInfo} />
      <LoadingSimpleBackdrop openLoadingSimpleBackdrop={loadingBackdrop} />

      <Box className={classes.missionHeader}>
        <Box>
          <Box className={classes.missionObject}>
            <strong>Objet de la mission :</strong> {mission?.mission_title || '-'}
          </Box>
        </Box>
        {provisionalFuel?.provisional_fuel_state === 'PENDING' && (
          <Chip
            icon={<AccessTimeIcon />}
            label="En attente de confirmation"
            className={classes.pendingChip}
            variant="outlined"
          />
        )}
        {provisionalFuel?.provisional_fuel_state === 'REFUSED' && (
          <Chip
            label="Carburant provisionnel refusé"
            color="error"
            variant="outlined"
          />
        )}
      </Box>

      {provisionalFuel?.provisional_fuel_state === 'REFUSED' && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <strong>Motif du refus : </strong>
          {refusalObservation || 'Aucun motif de refus renseigné.'}
        </Alert>
      )}

      <Alert severity="info" className={classes.scrollAlert}>
        <strong>Veuillez défiler horizontalement le tableau pour voir toutes les colonnes.</strong>
      </Alert>
      {transports.length !== 0 ? (
        <Box className={classes.contentSection}>
          <Box className={classes.tabsContainer}>
            <Tabs
              value={selectedTransportTab}
              onChange={(_, nextTab) => setSelectedTransportTab(nextTab)}
              variant="scrollable"
              scrollButtons="auto"
              className={classes.tabs}
            >
              {transports.map((transport, index) => (
                <Tab
                  key={transport.transport_id}
                  icon={<DirectionsCarIcon />}
                  iconPosition="start"
                  label={transport?.vhc_imm || transport?.rent_type || `Transport ${index + 1}`}
                />
              ))}
            </Tabs>
          </Box>

          <fieldset className={classes.fieldset}>
            <legend className={classes.fieldsetLegend}>
              {selectedTransport?.vhc_imm || selectedTransport?.rent_type || 'Transport'}
            </legend>
            {selectedTransport && <TransportData transport={selectedTransport} />}
            <RentalChargeTable
              transport={selectedTransport}
              destinations={transportDestinations}
            />
            <Box className={classes.distributionHeader}>
              <h2 className={classes.distributionTitle}>Répartition par destination</h2>
              {/* <Box className={classes.roundingInfo}>
                <InfoOutlinedIcon className={classes.roundingIcon} />
                <span>Les montants sont arrondis à la centaine la plus proche.</span>
              </Box> */}
            </Box>
            <TableContainer className={classes.tableWrapper}>
              <Table size="small" className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell className={`${classes.tableHeaderCell} ${classes.dateCell}`}>Date(s)</TableCell>
                  <TableCell className={classes.tableHeaderCell}>Lieu de départ</TableCell>
                  <TableCell className={classes.tableHeaderCell}>Lieu d'arrivée</TableCell>
                  <TableCell className={classes.tableHeaderCell}>Type</TableCell>
                  <TableCell className={classes.tableHeaderCell}>Missionnaire(s)</TableCell>
                  <TableCell className={classes.tableHeaderCell}>Estimation Kilométrage</TableCell>
                  <TableCell className={classes.tableHeaderCell}>Consommation en l</TableCell>
                  <TableCell className={classes.tableHeaderCell}>Prix unitaire en Ar</TableCell>
                  {financementColumns.map((fin, finIndex) => (
                    <TableCell
                      key={buildFinancementKey(fin, finIndex)}
                      className={`${classes.tableHeaderCell} ${classes.tdBorderedLeft}`}
                    >
                      {fin?.code_financement_mis || '-'}
                    </TableCell>
                  ))}
                  <TableCell className={classes.tableHeaderCell}>Carburant en Ar</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {isLoadingSkeleton ? (
                    Array.from({ length: 3 }).map((_, skeletonIndex) => (
                      <TableRow key={`skeleton-${skeletonIndex}`}>
                        {Array.from({ length: 9 + financementColumns.length }).map((__, cellIndex) => (
                          <TableCell key={`skeleton-cell-${skeletonIndex}-${cellIndex}`}>
                            <Skeleton variant="rectangular" height={24} />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    transportDestinations.map((dest, index) => {
                      const rowEntry = dest || {};
                      const rowKey = buildRowKey(selectedTransport.transport_id, dest, index);
                      const rowLineFinancements = getDestinationLineFinancements(dest, selectedTransport);
                      const rowLineFinancementKeys = rowLineFinancements.map((financement, lineIndex) =>
                        buildFinancementKey(financement, lineIndex)
                      );
                      const rowLineFinancementsByKey = rowLineFinancements.reduce((acc, financement, lineIndex) => {
                        acc[buildFinancementKey(financement, lineIndex)] = financement;
                        return acc;
                      }, {});
                      const baseAmount = parseNumber(
                        dest?.mission_timesheet_details_amount,
                        0
                      );
                      const rowDistributionTotal = financementColumns.reduce(
                        (total, financement, finIndex) => {
                          if (!financement) {
                            return total;
                          }
                          const finKey = buildFinancementKey(financement, finIndex);
                          const lineFinancement = rowLineFinancementsByKey[finKey];
                          if (!lineFinancement) {
                            return total;
                          }
                          const fallbackPercent = formatPercentValue(
                            lineFinancement?.percentage_rate
                          );
                          const percentage = parseNumber(
                            getRepartitionValue(rowKey, finKey, fallbackPercent),
                            0
                          );
                          return total + getDistributionAmount(
                            rowKey,
                            lineFinancement,
                            baseAmount,
                            percentage
                          );
                        },
                        0
                      );

                      return (
                        <TableRow key={rowKey}>
                          <TableCell className={classes.dateCell}>
                            {formatDate(dest.date_begin) === formatDate(dest.date_end)
                              ? formatDate(dest.date_begin)
                              : `${formatDate(dest.date_begin)} - ${formatDate(dest.date_end)}`}
                              <br/>
                             <span className={classes.timeText}> {dest.heure_begin} - {dest.heure_end}</span>
                          </TableCell>
                          <TableCell className={`${classes.locationCell} ${classes.tdBorderedLeft}`}>
                            {getCombinedLocationLabel(rowEntry, dest, 'depart')}
                          </TableCell>
                          <TableCell className={classes.locationCell}>
                            {getCombinedLocationLabel(rowEntry, dest, 'arrivee')}
                          </TableCell>
                          <TableCell className={`${classes.typeCell} ${classes.tdBorderedLeft}`}>
                            {dest.type === "DM" ? (
                              <Chip icon={<CardTravelIcon />} label="Mission" variant="outlined" className={classes.typeChip} />
                            ) : (
                              <Chip
                                icon={<TimeToLeaveIcon />}
                                className={classes.typeChip}
                                label="Délai de route"
                                variant="outlined"
                              />
                            )}
                          </TableCell>
                          <TableCell className={classes.missionaryCell}>
                            {dest.missionnaries && dest.missionnaries.length > 0 ? (
                              dest.missionnaries.map((missionary, idx) => (
                                <li key={`dest-mis-${rowKey}-${idx}`}>{missionary.name}</li>
                              ))
                            ) : selectedTransport.travelers && selectedTransport.travelers.length > 0 ? (
                              selectedTransport.travelers.map((missionary, idx) => (
                                <li key={`trans-mis-${rowKey}-${idx}`}>{missionary.name}</li>
                              ))
                            ) : (
                              <label>Aucun missionnaire</label>
                            )}
                          </TableCell>
                          <TableCell className={`${classes.numberCell} ${classes.tdBorderedLeft}`}>
                            <label><strong>{formatWithThousandSeparator(dest?.distance_km || "")}</strong></label>
                          </TableCell>
                          <TableCell className={classes.numberCell}>
                            <label><strong>{formatWithThousandSeparator(dest.mission_timesheet_details_consumption)}</strong></label>
                          </TableCell>
                          <TableCell className={classes.numberCell}>
                            <label><strong>{selectedTransport.price_per_liter}</strong></label>
                          </TableCell>
                          {financementColumns.map((fin, finIndex) => {
                            if (!fin) {
                              return (
                                <TableCell
                                  key={buildFinancementKey(fin, finIndex)}
                                  sx={{ textAlign: "center" }}
                                  className={classes.tdBorderedLeft}
                                >
                                  -
                                </TableCell>
                              );
                            }

                            const finKey = buildFinancementKey(fin, finIndex);
                            const lineFinancement = rowLineFinancementsByKey[finKey];
                            if (!lineFinancement) {
                              return (
                                <TableCell key={finKey} sx={{ textAlign: "center" }} className={classes.tdBorderedLeft}>
                                  -
                                </TableCell>
                              );
                            }

                            const fallbackPercent = formatPercentValue(lineFinancement?.percentage_rate);
                            const value = getRepartitionValue(rowKey, finKey, fallbackPercent);
                            const normalizedPercent = parseNumber(value, 0);
                            const distributionAmount = getDistributionAmount(
                              rowKey,
                              lineFinancement,
                              baseAmount,
                              normalizedPercent
                            );
                            const isRowEditable = rowLineFinancementKeys.length > 1;

                            return (
                              <TableCell key={finKey} sx={{ textAlign: "center" }} className={classes.tdBorderedLeft}>
                                <Box className={classes.percentageContent}>
                                  <TextField
                                    size="small"
                                    variant="outlined"
                                    value={value}
                                    disabled={!isRowEditable}
                                    onChange={(event) => {
                                      const rawValue = event.target.value.replace(',', '.');
                                      if (rawValue === '' || /^\d*\.?\d*$/.test(rawValue)) {
                                        handleRepartitionChange(
                                          rowKey,
                                          finKey,
                                          rowLineFinancementKeys,
                                          rawValue
                                        );
                                      }
                                    }}
                                    className={classes.percentageInput}
                                    inputProps={{ inputMode: 'decimal', style: { textAlign: 'center' } }}
                                    InputProps={{
                                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                    }}
                                  />
                                  <Box component="span" className={classes.percentageAmount}>
                                    {formatWithThousandSeparator(distributionAmount, { fallback: '0' })} Ar
                                  </Box>
                                </Box>
                              </TableCell>
                            );
                          })}
                          <TableCell className={classes.numberCell}>
                            {formatWithThousandSeparator(rowDistributionTotal, {
                              fallback: '0',
                            })}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>

                <TableFooter>
                  <TableRow className={classes.totalRow}>
                    <TableCell colSpan={5} sx={{ textAlign: "right", fontWeight: "bold" }}>Total</TableCell>
                    <TableCell sx={{ textAlign: "center", fontWeight: "bold" }}>
                      {formatWithThousandSeparator(totalDistanceKm, { fallback: '0' })}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontWeight: "bold" }}>
                      {formatWithThousandSeparator(totalConsumption, { fallback: '0' })}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontWeight: "bold" }}>
                      {formatWithThousandSeparator(transportPricePerLiter, { fallback: '0' })}
                    </TableCell>
                    {financementColumns.map((fin, finIndex) => {
                      if (!fin) {
                        return (
                          <TableCell
                            key={buildFinancementKey(fin, finIndex)}
                            sx={{ textAlign: "center", fontWeight: "bold" }}
                          >
                            -
                          </TableCell>
                        );
                      }
                      const finKey = buildFinancementKey(fin, finIndex);
                      return (
                        <TableCell
                          key={finKey}
                          sx={{ textAlign: "center", fontWeight: "bold" }}
                        >
                          {formatWithThousandSeparator(financementTotals[finKey] || 0, { fallback: '0' })}
                        </TableCell>
                      );
                    })}
                    <TableCell sx={{ textAlign: "center", fontWeight: "bold" }}>
                      {formatWithThousandSeparator(totalAmount, { fallback: '0' })}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </fieldset>
        </Box>
      ) : hasTimesheetContext && !isLoadingSkeleton ? (
        <Alert severity="error" className={classes.emptyAlert}>
          Aucun transport n'est associé à cette mission.
        </Alert>
      ) : (
        <Alert severity="info" className={classes.emptyAlert}>
          Aucun agenda n'est associé à cette mission.
        </Alert>
      )}

      <Box className={classes.actionBar}>
        <Button
          variant="outlined"
          color="primary"
          size="medium"
          onClick={() => navigate(-1)}
        >
          Annuler
        </Button>
        {!isLastTransportTab ? (
          <Button
            variant="contained"
            color="primary"
            size="medium"
            onClick={() => setSelectedTransportTab((prev) => Math.min(prev + 1, transports.length - 1))}
            disabled={!transports.length}
          >
            Transport suivant
          </Button>
        ) : (
          !isPendingState &&
          <Button
            variant="contained"
            color="success"
            size="medium"
            onClick={() => handleConfirmDistrubution()}
            disabled={disabled || !transports.length }
          >
            Confirmer la distribution
          </Button>
        )}
      </Box>
    </Paper>

    <SuccesDialog open={callbackDialog.open && callbackDialog.type == "success"} onClose={callbackDialog.onClose} message={callbackDialog.message} />
    <ErrorDialog open={callbackDialog.open && callbackDialog.type == "error"} onClose={callbackDialog.onClose} message={callbackDialog.message} />
    </Box>
  );
}

export default FuelProvisionalConfirm;
